import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { updateThemeData } from "../../functions/updateThemeData";

const MAX_COLORS = 9;
const NAME_MAX = 30;
const VALUE_MAX = 40;
const DEF_MAX = 200;

function normalizeColors(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, MAX_COLORS).map((c, i) => ({
    id: String(c?.id || String(i + 1)),
    color_name: String(c?.color_name || "").trim(),
    color_code: String(c?.color_code || "#888888").trim(),
    color_value: String(c?.color_value || "").trim(),
    color_definitions: String(c?.color_definitions || "").trim(),
  }));
}

function nextId(list) {
  const nums = list.map((c) => Number(c.id)).filter((n) => Number.isFinite(n) && n > 0);
  return String((nums.length ? Math.max(...nums) : 0) + 1);
}

export default function ColorsEditor({ data, currentTheme, adminToken, questions = [] }) {
  const dispatch = useDispatch();
  const [colors, setColors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({
    color_name: "",
    color_code: "#E25569",
    color_value: "",
    color_definitions: "",
  });

  useEffect(() => {
    if (!data) return;
    if ("colors" in data) setColors(normalizeColors(data.colors));
  }, [data]);

  const persist = async (next, msg = "Colours saved.") => {
    if (!currentTheme || !adminToken) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return false;
    }
    setSaving(true);
    try {
      await updateThemeData({
        payload: { data: { colors: next }, currentTheme },
        token: adminToken,
      });
      setColors(next);
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", msg, "success");
      return true;
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save colours.", "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    if (colors.length >= MAX_COLORS) {
      Swal.fire("Limit", `Maximum ${MAX_COLORS} colours allowed.`, "warning");
      return;
    }
    setForm({
      color_name: "",
      color_code: "#E25569",
      color_value: "",
      color_definitions: "",
    });
    setModal({ mode: "add" });
  };

  const openEdit = (index) => {
    const c = colors[index];
    setForm({
      color_name: c.color_name,
      color_code: c.color_code || "#E25569",
      color_value: c.color_value,
      color_definitions: c.color_definitions,
    });
    setModal({ mode: "edit", index });
  };

  const validateForm = () => {
    const name = form.color_name.trim();
    const value = form.color_value.trim();
    const def = form.color_definitions.trim();
    const code = form.color_code.trim();
    if (!name) return "Colour name is required.";
    if (name.length > NAME_MAX) return `Colour name max ${NAME_MAX} characters.`;
    if (!/^#[0-9A-Fa-f]{6}$/.test(code)) return "Display colour must be a hex like #E25569.";
    if (!value) return "Value is required.";
    if (value.length > VALUE_MAX) return `Value max ${VALUE_MAX} characters.`;
    if (!def) return "Definition is required.";
    if (def.length > DEF_MAX) return `Definition max ${DEF_MAX} characters.`;

    const editIndex = modal?.mode === "edit" ? modal.index : -1;
    const dupName = colors.findIndex(
      (c, i) => i !== editIndex && c.color_name.toLowerCase() === name.toLowerCase()
    );
    if (dupName >= 0) return "Colour name must be unique.";
    const dupValue = colors.findIndex(
      (c, i) => i !== editIndex && c.color_value.toLowerCase() === value.toLowerCase()
    );
    if (dupValue >= 0) return "Colour value must be unique (used as question answers).";
    return null;
  };

  const handleSaveModal = async () => {
    const err = validateForm();
    if (err) {
      Swal.fire("Invalid", err, "warning");
      return;
    }
    const entry = {
      id: modal.mode === "edit" ? colors[modal.index].id : nextId(colors),
      color_name: form.color_name.trim(),
      color_code: form.color_code.trim(),
      color_value: form.color_value.trim(),
      color_definitions: form.color_definitions.trim(),
    };

    let next;
    if (modal.mode === "add") {
      next = [...colors, entry];
    } else {
      const oldValue = colors[modal.index].color_value;
      next = colors.map((c, i) => (i === modal.index ? entry : c));
      // Keep question answers in sync when value text changes
      if (oldValue !== entry.color_value && Array.isArray(questions) && questions.length) {
        const updatedQuestions = questions.map((q) => ({
          ...q,
          correct_answers: (q.correct_answers || []).map((a) =>
            a === oldValue ? entry.color_value : a
          ),
        }));
        try {
          await updateThemeData({
            payload: {
              data: { colors: next, questions: updatedQuestions },
              currentTheme,
            },
            token: adminToken,
          });
          setColors(next);
          await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
          setModal(null);
          Swal.fire("Saved", "Colour and linked answers updated.", "success");
          return;
        } catch (e) {
          Swal.fire("Error", e.message || "Failed to save.", "error");
          return;
        }
      }
    }

    const ok = await persist(next, modal.mode === "add" ? "Colour added." : "Colour updated.");
    if (ok) setModal(null);
  };

  const handleDelete = async (index) => {
    const c = colors[index];
    const usedBy = (questions || []).filter((q) =>
      (q.correct_answers || []).includes(c.color_value)
    );
    if (usedBy.length) {
      Swal.fire(
        "In use",
        `“${c.color_value}” is used by ${usedBy.length} question(s). Remove it from those answers first.`,
        "warning"
      );
      return;
    }
    const confirm = await Swal.fire({
      title: "Delete colour?",
      text: `${c.color_name} — ${c.color_value}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;
    await persist(
      colors.filter((_, i) => i !== index),
      "Colour deleted."
    );
  };

  return (
    <div className="catmgr-card" style={{ marginBottom: 24 }} data-tour="coc-colors">
      <div className="catmgr-header" style={{ marginBottom: 16, border: "none", padding: 0 }}>
        <div>
          <h2 className="catmgr-title">
            <i className="fa-solid fa-palette" /> &nbsp;Game Colours
          </h2>
          <p className="catmgr-subtitle">
            {colors.length} / {MAX_COLORS} colours · values become selectable answers
          </p>
        </div>
        <div className="catmgr-header-right">
          <button
            type="button"
            className="catmgr-btn catmgr-btn-outline"
            onClick={openAdd}
            disabled={saving || colors.length >= MAX_COLORS}
          >
            <i className="fa-solid fa-plus" /> Add Colour
          </button>
        </div>
      </div>

      {colors.length === 0 ? (
        <div className="catmgr-empty">No colours yet. Add up to {MAX_COLORS}.</div>
      ) : (
        <div className="catmgr-items-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
          {colors.map((c, index) => (
            <div className="catmgr-item-card" key={c.id}>
              <div className="catmgr-item-info" style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: c.color_code,
                      border: "1px solid #ddd",
                      flexShrink: 0,
                    }}
                  />
                  <span className="catmgr-item-name">{c.color_name}</span>
                </div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{c.color_value}</div>
                <div className="catmgr-item-filename" title={c.color_definitions}>
                  {c.color_definitions}
                </div>
                <div className="catmgr-cat-key">{c.color_code}</div>
              </div>
              <div className="catmgr-item-btns">
                <button
                  type="button"
                  className="catmgr-icon-btn catmgr-icon-btn-sm"
                  title="Edit"
                  onClick={() => openEdit(index)}
                  disabled={saving}
                >
                  <i className="fa-solid fa-pen" />
                </button>
                <button
                  type="button"
                  className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                  title="Delete"
                  onClick={() => handleDelete(index)}
                  disabled={saving}
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal ? (
        <>
          <div className="catmgr-backdrop" onClick={() => setModal(null)} />
          <div className="catmgr-modal catmgr-modal-wide">
            <h3>{modal.mode === "add" ? "Add Colour" : "Edit Colour"}</h3>

            <label className="catmgr-modal-label">Colour name</label>
            <input
              className="catmgr-modal-input"
              value={form.color_name}
              onChange={(e) => setForm((f) => ({ ...f, color_name: e.target.value.slice(0, NAME_MAX) }))}
              placeholder="e.g. Red"
              maxLength={NAME_MAX}
            />

            <label className="catmgr-modal-label">Display colour</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <input
                type="color"
                value={form.color_code}
                onChange={(e) => setForm((f) => ({ ...f, color_code: e.target.value }))}
                style={{ width: 48, height: 36, border: "none", cursor: "pointer" }}
              />
              <input
                className="catmgr-modal-input"
                style={{ marginBottom: 0, flex: 1 }}
                value={form.color_code}
                onChange={(e) => setForm((f) => ({ ...f, color_code: e.target.value }))}
                placeholder="#E25569"
              />
            </div>

            <label className="catmgr-modal-label">Value</label>
            <input
              className="catmgr-modal-input"
              value={form.color_value}
              onChange={(e) => setForm((f) => ({ ...f, color_value: e.target.value.slice(0, VALUE_MAX) }))}
              placeholder="e.g. Confidence"
              maxLength={VALUE_MAX}
            />
            <p className="catmgr-modal-hint">This is the answer key used when assigning questions.</p>

            <label className="catmgr-modal-label">Definition</label>
            <textarea
              className="catmgr-modal-input"
              rows={3}
              value={form.color_definitions}
              onChange={(e) =>
                setForm((f) => ({ ...f, color_definitions: e.target.value.slice(0, DEF_MAX) }))
              }
              placeholder="Short meaning of this value"
              maxLength={DEF_MAX}
            />

            <div className="catmgr-modal-actions">
              <button type="button" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={handleSaveModal} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
